import { useCallback } from 'react';
import { Platform, View } from 'react-native';

import { useFocusEffect, useNavigation } from '@react-navigation/native';

import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { useShareEntry } from '@/feature/entries-pickem/components/ShareEntryProvider';
import { usePopSelf } from '@/hooks/use-pop-self';
import { logger } from '@/utils/logging';

import { CreateLinkResponse, uploadImageMetaToLink } from '../utils/social-link';
import { captureViewShot } from '../utils/view-shot';

const LOG_TAG = '[ShareEntryCapture]';

export const useShareEntryCapture = (viewRef: React.RefObject<View | null>) => {
    const navigation = useNavigation();
    const { showToast } = useAlerts();
    const { showShareEntryModal, dismissShareEntryModal } = useShareEntry();
    const removeShareScreen = usePopSelf();

    // close the share flow if anything is pushed over this screen
    useFocusEffect(
        useCallback(
            () => () => {
                dismissShareEntryModal();
                removeShareScreen();
            },
            [dismissShareEntryModal, removeShareScreen]
        )
    );

    const captureShareImage = useCallback(
        () => captureViewShot(viewRef, { snapshotContentContainer: false, format: 'png' }),
        [viewRef]
    );

    const presentShareLink = useCallback(
        (screenshot: string, link: CreateLinkResponse) => {
            if (!navigation.isFocused()) {
                return;
            }
            showShareEntryModal({
                showToast,
                imageUri: screenshot,
                link: link.shortUrl,
                onDismiss: removeShareScreen,
            });
            if (Platform.OS !== 'web') {
                // lower quality screenshot to upload as the short link's image preview
                captureViewShot(viewRef, {
                    snapshotContentContainer: false,
                    format: 'jpg',
                    quality: 0.25,
                })
                    .then(res => uploadImageMetaToLink(link.id, res))
                    .catch(err => logger.warn(LOG_TAG, 'Could not upload link preview', err));
            }
        },
        [navigation, removeShareScreen, showShareEntryModal, showToast, viewRef]
    );

    return { captureShareImage, presentShareLink, removeShareScreen };
};
